import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { CalculateAlertTime } from 'app/pipes/pipes';
import { AppRecipient } from 'app/services/control-room.service';

/**
 * The recipients the alarm would reach, so the operator can weigh sending it
 * anyway against how long ago each of them was last seen.
 */
export interface SendToAppConfirmData {
  recipients: AppRecipient[];
  // true only when the alarm route itself resolved to nobody. The recipient
  // list can be empty for other reasons - an alert that does not report its
  // targets, or a route that targets only the operator's own session - and
  // neither of those is the route reaching nobody. Read from the alert rather
  // than from the list below, so the dialog and the card say the same thing.
  routeIsEmpty: boolean;
}

/**
 * Confirmation dialog shown when an alarm is about to be forwarded to the EMA
 * app with no recipient available. Sending is allowed - the operator may know
 * something the presence data does not - but not by accident: the dialog closes
 * with `true` only on an explicit confirmation, and with `false` or `undefined`
 * on cancel or dismissal.
 */
@Component({
    selector: 'app-send-to-app-confirm-dialog',
    templateUrl: './send-to-app-confirm-dialog.component.html',
    styleUrls: ['./send-to-app-confirm-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslateModule, CalculateAlertTime]
})
export class SendToAppConfirmDialogComponent {

  // a group-targeted alarm route can resolve to dozens of recipients, which is
  // an unreadable list at the moment an operator is deciding in a hurry
  private static readonly VISIBLE_RECIPIENTS = 5;

  constructor(
    public dialogRef: MatDialogRef<SendToAppConfirmDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: SendToAppConfirmData
  ) { }

  /**
   * Recipients the session's team knows, which are the only ones there is
   * anything to say about. A row reading "Unknown user - no data" tells an
   * operator nothing, and three of them tell them nothing three times. The
   * ones left out are in the verdict already: they count as out of reach.
   */
  private get namedRecipients(): AppRecipient[] {
    return this.data.recipients.filter((recipient) => recipient.known);
  }

  get visibleRecipients(): AppRecipient[] {
    return this.namedRecipients.slice(0, SendToAppConfirmDialogComponent.VISIBLE_RECIPIENTS);
  }

  get hiddenRecipientCount(): number {
    return Math.max(0, this.namedRecipients.length - SendToAppConfirmDialogComponent.VISIBLE_RECIPIENTS);
  }

  cancel() {
    this.dialogRef.close(false);
  }

  confirm() {
    this.dialogRef.close(true);
  }
}
